import { createRoot, type Root } from 'react-dom/client';
import { BookingWidget } from './components/BookingWidget';
import { ensureFonts } from './lib/fonts';
import type {
  YogaBookingConfig,
  YogaBookingControls,
  YogaBookingHandle,
} from './types';
import './styles/tokens.css';

const DEFAULT_API_URL = 'http://localhost:3000';
const MOUNT_SELECTOR = '#yoga-booking-root, [data-yoga-booking]';

interface MountedInstance {
  root: Root;
  element: HTMLElement;
  controls: YogaBookingControls | null;
  triggerCleanup?: () => void;
}

interface YogaBookingGlobals {
  instances: Map<HTMLElement, MountedInstance>;
  observerStarted: boolean;
}

declare global {
  interface Window {
    __yogaBooking?: YogaBookingGlobals;
  }
}

function getGlobals(): YogaBookingGlobals {
  if (!window.__yogaBooking) {
    window.__yogaBooking = {
      instances: new Map(),
      observerStarted: false,
    };
  }
  return window.__yogaBooking;
}

function parseShowTrigger(
  overrides: Partial<YogaBookingConfig> | undefined,
  element: HTMLElement,
): boolean {
  if (overrides?.showTrigger !== undefined) {
    return overrides.showTrigger;
  }

  const raw =
    element.dataset.showTrigger ?? element.getAttribute('data-show-trigger');

  if (raw === null || raw === undefined || raw === '') {
    return true;
  }

  return raw !== 'false' && raw !== '0';
}

function readConfig(
  element: HTMLElement,
  overrides?: Partial<YogaBookingConfig>,
): YogaBookingConfig {
  const studioSlug =
    overrides?.studioSlug ??
    element.dataset.studioSlug ??
    element.getAttribute('data-studio-slug');

  if (!studioSlug) {
    throw new Error(
      'Yoga Booking Widget: data-studio-slug fehlt am Mount-Element.',
    );
  }

  const apiUrl =
    overrides?.apiUrl ??
    element.dataset.apiUrl ??
    element.getAttribute('data-api-url') ??
    DEFAULT_API_URL;

  const triggerSelector =
    overrides?.triggerSelector ??
    element.dataset.triggerSelector ??
    element.getAttribute('data-trigger-selector') ??
    undefined;

  return {
    studioSlug,
    apiUrl,
    showTrigger: parseShowTrigger(overrides, element),
    triggerSelector: triggerSelector || undefined,
    mountElement: element,
  };
}

function bindTriggers(selector: string, open: () => void): () => void {
  // Event delegation so buttons added later (Webstudio hydration) still work.
  const handler = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const trigger = target.closest(selector);
    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    // Ignore clicks that originate inside an already-open widget dialog.
    if (trigger.closest('[role="dialog"]')) {
      return;
    }

    event.preventDefault();
    open();
  };

  // Capture phase: host site handlers often stopPropagation on bubble.
  document.addEventListener('click', handler, true);
  return () => document.removeEventListener('click', handler, true);
}

function destroyInstance(element: HTMLElement): void {
  const { instances } = getGlobals();
  const instance = instances.get(element);
  if (!instance) {
    return;
  }
  instance.triggerCleanup?.();
  instance.root.unmount();
  instances.delete(element);
  element.replaceChildren();
}

function cleanupDisconnectedInstances(): void {
  const { instances } = getGlobals();
  for (const element of [...instances.keys()]) {
    if (!element.isConnected) {
      destroyInstance(element);
    }
  }
}

function mountWidget(
  element: HTMLElement,
  config: YogaBookingConfig,
): MountedInstance {
  const { instances } = getGlobals();
  ensureFonts();

  destroyInstance(element);

  element.classList.add('yoga-booking-widget');

  const mountPoint = document.createElement('div');
  element.appendChild(mountPoint);

  const instance: MountedInstance = {
    root: createRoot(mountPoint),
    element,
    controls: null,
  };

  let pendingOpen = false;

  const open = () => {
    if (instance.controls) {
      instance.controls.open();
      return;
    }
    pendingOpen = true;
  };

  if (config.triggerSelector) {
    instance.triggerCleanup = bindTriggers(config.triggerSelector, open);
  }

  instances.set(element, instance);

  instance.root.render(
    <BookingWidget
      studioSlug={config.studioSlug}
      apiUrl={config.apiUrl ?? DEFAULT_API_URL}
      showTrigger={config.showTrigger !== false}
      onReady={(controls) => {
        instance.controls = controls;
        if (pendingOpen) {
          pendingOpen = false;
          controls.open();
        }
      }}
    />,
  );

  return instance;
}

function toHandle(element: HTMLElement): YogaBookingHandle {
  return {
    open: () => {
      getGlobals().instances.get(element)?.controls?.open();
    },
    close: () => {
      getGlobals().instances.get(element)?.controls?.close();
    },
    destroy: () => {
      destroyInstance(element);
    },
  };
}

function mountAllPresent(): void {
  cleanupDisconnectedInstances();

  const elements = document.querySelectorAll<HTMLElement>(MOUNT_SELECTOR);
  const { instances } = getGlobals();

  elements.forEach((element) => {
    if (instances.has(element)) {
      return;
    }

    try {
      const config = readConfig(element);
      mountWidget(element, config);
    } catch {
      // Skip elements without studio slug during auto-init.
    }
  });
}

function startMountObserver(): void {
  const globals = getGlobals();
  if (globals.observerStarted || typeof MutationObserver === 'undefined') {
    return;
  }
  globals.observerStarted = true;

  // Webstudio SPA navigation does not fire DOMContentLoaded again — watch the DOM.
  const observer = new MutationObserver(() => {
    mountAllPresent();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

export function initYogaBooking(config: YogaBookingConfig): YogaBookingHandle {
  const element =
    config.mountElement ??
    document.querySelector<HTMLElement>(MOUNT_SELECTOR) ??
    undefined;

  if (!element) {
    throw new Error(
      `Yoga Booking Widget: Kein Mount-Element gefunden (${MOUNT_SELECTOR}).`,
    );
  }

  const resolved = readConfig(element, config);
  mountWidget(element, resolved);
  startMountObserver();
  return toHandle(element);
}

/**
 * Auto-init for script-tag embeds (Webstudio HTML Embed, etc.).
 * Does not use DOMContentLoaded — that never fires on client-side page transitions.
 */
export function autoInitYogaBooking(): void {
  if (typeof document === 'undefined') {
    return;
  }

  // Run immediately (Webstudio: place embed after needed DOM, or rely on observer).
  mountAllPresent();
  startMountObserver();
}

export type { YogaBookingConfig, YogaBookingHandle };
